// Club-configurable course grid.
//
// DBSC's hardcoded 16×8 A–R letter grid (courseConstants.js) is one shape among
// several clubs use. This module derives a grid shape from a small per-club
// config so the committee CardGrid can render 16 / 8 / 4 wind directions, pick a
// default course-naming scheme, and number courses either per-direction (1..N)
// or with sector-grouped GAPS (11–14, 21–24, …). courseConstants.js stays the
// canonical 16-letter table; everything here is derived from it so the parsers
// and tests that depend on those exports are untouched.
//
// The shipped app reads each course's directionCode + bearing (and, for named
// courses, windDirection/name) and is grid-config agnostic — it snaps a bearing
// to the nearest supplied DirectionEntry. So this is a committee-only change:
// the app needs no knowledge of the club's grid config. (ra-gbrd.20)

import {
  DIRECTION_LETTERS,
  DIRECTION_LABELS,
  DIRECTION_BEARINGS,
} from './courseConstants.js';

export const DIRECTION_COUNTS = [16, 8, 4];
export const NAME_SCHEMES = ['letter', 'compass', 'number'];
export const NUMBERING_MODES = ['per-direction', 'global-gapped'];

// Reproduces the DBSC 16×8 A–R letter grid exactly. Used as the default for any
// club that hasn't saved a course_config, so existing behaviour is unchanged.
export const DEFAULT_GRID_CONFIG = Object.freeze({
  directionCount: 16,
  nameScheme: 'letter',
  numbering: 'per-direction',
  coursesPerDirection: 8,
});

/**
 * The subset of the canonical 16-letter table for a given direction count.
 * Steps the 16-letter table by 16/count so the cardinals stay evenly spaced:
 *   16 → A B C D E F G H J K L M N P Q R
 *    8 → A C E G J L N Q
 *    4 → A E J N
 * @param {number} count 16 | 8 | 4
 * @returns {string[]}
 */
export function directionLettersFor(count) {
  const step = 16 / count;
  if (!Number.isInteger(step) || step < 1) {
    throw new Error(`Unsupported directionCount: ${count} (must be a divisor of 16)`);
  }
  const letters = [];
  for (let i = 0; i < DIRECTION_LETTERS.length; i += step) {
    letters.push(DIRECTION_LETTERS[i]);
  }
  return letters;
}

/**
 * Validate + normalize a partial config into a complete, frozen-shape config.
 * Throws on an unsupported value so a bad Firestore document can be caught and
 * defaulted rather than silently producing a broken grid.
 * @param {Partial<typeof DEFAULT_GRID_CONFIG>} opts
 */
export function buildGridConfig(opts = {}) {
  const cfg = { ...DEFAULT_GRID_CONFIG, ...opts };
  if (!DIRECTION_COUNTS.includes(cfg.directionCount)) {
    throw new Error(`directionCount must be one of ${DIRECTION_COUNTS.join(', ')}`);
  }
  if (!NAME_SCHEMES.includes(cfg.nameScheme)) {
    throw new Error(`nameScheme must be one of ${NAME_SCHEMES.join(', ')}`);
  }
  if (!NUMBERING_MODES.includes(cfg.numbering)) {
    throw new Error(`numbering must be one of ${NUMBERING_MODES.join(', ')}`);
  }
  const n = Math.floor(cfg.coursesPerDirection);
  // Cap at 9 so global-gapped numbers (sectorOrdinal*10 + col) stay inside their
  // sector's 10-block and never collide with the next sector's range.
  if (!Number.isFinite(n) || n < 1 || n > 9) {
    throw new Error('coursesPerDirection must be an integer 1–9');
  }
  return {
    directionCount: cfg.directionCount,
    nameScheme: cfg.nameScheme,
    numbering: cfg.numbering,
    coursesPerDirection: n,
  };
}

/**
 * The course numbers (the `courseNumber` identity stored on each course) for one
 * direction row.
 *   per-direction : [1, 2, …, coursesPerDirection]
 *   global-gapped : sector-grouped with gaps, e.g. row 0 → 11,12,13,14;
 *                   row 1 → 21,22,23,24; … (sectorOrdinal*10 + col)
 * @param {object} config
 * @param {number} dirIdx zero-based row index within the configured directions
 * @returns {number[]}
 */
export function courseNumbersFor(config, dirIdx) {
  const cfg = config || DEFAULT_GRID_CONFIG;
  const count = cfg.coursesPerDirection ?? DEFAULT_GRID_CONFIG.coursesPerDirection;
  if (cfg.numbering === 'global-gapped') {
    const base = (dirIdx + 1) * 10; // sector ordinal block
    return Array.from({ length: count }, (_, i) => base + i + 1);
  }
  return Array.from({ length: count }, (_, i) => i + 1);
}

/** Cardinal abbreviation for a direction letter, e.g. 'H' → 'SSE'. */
export function compassAbbrevFor(letter) {
  const label = DIRECTION_LABELS[letter];
  return label ? label.split(' ')[0] : letter;
}

/**
 * The default human-facing name for a cell under the club's chosen scheme.
 * Numbers are NOT zero-padded (Chris, ra-npo7): C3, not C03.
 *   letter  → `${dir}${n}`             e.g. C3   (DBSC-style)
 *   compass → `${cardinal}${n}`        e.g. SSE3
 *   number  → `${n}`                   e.g. 3   (or the gapped number, e.g. 11)
 */
export function defaultCourseName(config, dir, num) {
  const cfg = config || DEFAULT_GRID_CONFIG;
  const n = String(num);
  switch (cfg.nameScheme) {
    case 'compass':
      return `${compassAbbrevFor(dir)}${n}`;
    case 'number':
      return n;
    case 'letter':
    default:
      return `${dir}${n}`;
  }
}

/**
 * True when the config is (value-)equal to the DBSC default. Used to keep the
 * flagship club's published bundle byte-identical: a default grid stamps no
 * `name` on grid courses (the directionCode+courseNumber already encodes it),
 * while any customized grid stamps a scheme-based default name on cell-create.
 */
export function isDefaultGridConfig(config) {
  if (!config) return true;
  return (
    config.directionCount === DEFAULT_GRID_CONFIG.directionCount &&
    config.nameScheme === DEFAULT_GRID_CONFIG.nameScheme &&
    config.numbering === DEFAULT_GRID_CONFIG.numbering &&
    config.coursesPerDirection === DEFAULT_GRID_CONFIG.coursesPerDirection
  );
}

/**
 * The rows to render for a config: one per configured direction, each carrying
 * its letter (the directionCode identity), display label/cardinal/bearing, and
 * the course numbers for its cells. The single source the CardGrid renders from.
 * @returns {{dir:string,rowIdx:number,label:string,cardinal:string,bearing:number,numbers:number[]}[]}
 */
export function directionRowsFor(config) {
  const cfg = config || DEFAULT_GRID_CONFIG;
  const letters = directionLettersFor(cfg.directionCount);
  return letters.map((letter, rowIdx) => ({
    dir: letter,
    rowIdx,
    label: DIRECTION_LABELS[letter] || letter,
    cardinal: compassAbbrevFor(letter),
    bearing: DIRECTION_BEARINGS[letter],
    numbers: courseNumbersFor(cfg, rowIdx),
  }));
}
